import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'

import {
  getDebugEventsAround,
  getFeedbackScreenshotUrl,
  getUserFeedback,
  updateFeedbackStatus,
  type ClientDebugEvent,
  type UserFeedbackRow,
} from '@/api/user_feedback'
import { PageHeader } from '@/components/layout/PageHeader'
import { Loader } from '@/components/ui/Loader'
import { Button } from '@/components/ui/Button'
import { Lightbox } from '@/components/ui/Lightbox'
import { TypeBadge } from '@/pages/UserFeedbackPage'
import { dateFnsLocale } from '@/utils/dateLocale'

/**
 * /user-feedback/:id — карточка обращения.
 *
 * Левая колонка: данные от пользователя (тип, описание, скриншоты, метаданные).
 * Правая колонка: timeline отладочных событий из `client_debug_events` по
 * `session_id` обращения в окне ±30 мин — то, что юзер делал прямо перед
 * отправкой багрепорта. Это даёт реальный контекст для воспроизведения.
 *
 * Действие админа: одно — переключатель «Отметить устранённым» ⇄ «Вернуть
 * в открытые». Опционально комментарий.
 */
export function UserFeedbackDetailPage() {
  const { t } = useTranslation()
  const { id = '' } = useParams<{ id: string }>()
  const qc = useQueryClient()

  const feedbackQ = useQuery({
    queryKey: ['user-feedback', 'detail', id],
    queryFn: () => getUserFeedback(id),
    enabled: !!id,
  })

  const debugQ = useQuery({
    queryKey: ['user-feedback', 'debug', id],
    queryFn: () =>
      getDebugEventsAround({
        sessionId: feedbackQ.data!.session_id,
        feedbackCreatedAt: feedbackQ.data!.created_at,
      }),
    enabled: !!feedbackQ.data,
    staleTime: 60_000,
  })

  // Локальный override комментария. null означает «юзер не редактировал →
  // показываем серверное значение из feedbackQ.data.admin_comment».
  // Так избегаем useEffect+setState для синхронизации (anti-pattern).
  const [localComment, setLocalComment] = useState<string | null>(null)
  const serverComment = feedbackQ.data?.admin_comment ?? ''
  const adminComment = localComment ?? serverComment
  const commentDirty = localComment !== null && localComment !== serverComment

  const mut = useMutation({
    mutationFn: updateFeedbackStatus,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['user-feedback'] })
      setLocalComment(null) // освобождаем override — поедет свежее серверное
    },
  })

  if (feedbackQ.isLoading || !feedbackQ.data) {
    return (
      <div>
        <BackLink />
        <PageHeader title={t('feedback.ticket')} />
        {feedbackQ.error ? (
          <Banner kind="error" text={t('feedback.loadError', { message: (feedbackQ.error as Error).message })} />
        ) : (
          <Loader />
        )}
      </div>
    )
  }

  const f = feedbackQ.data
  const isResolved = f.status === 'resolved'

  function toggleStatus() {
    const nextStatus = isResolved ? 'open' : 'resolved'
    mut.mutate({
      id: f.id,
      status: nextStatus,
      adminComment: commentDirty ? adminComment.trim() || null : undefined,
    })
  }

  function saveCommentOnly() {
    mut.mutate({
      id: f.id,
      status: f.status,
      adminComment: adminComment.trim() || null,
    })
  }

  return (
    <div>
      <BackLink />
      <PageHeader
        title={
          <span className="inline-flex items-center gap-3">
            <TypeBadge type={f.type} />
            <span>{t('feedback.ticket')}</span>
          </span>
        }
        subtitle={format(new Date(f.created_at), t('feedback.ticketDateFormat'), { locale: dateFnsLocale() })}
        actions={
          <Button
            variant={isResolved ? 'secondary' : 'primary'}
            onClick={toggleStatus}
            disabled={mut.isPending}
          >
            {mut.isPending
              ? t('feedback.saving')
              : isResolved
                ? t('feedback.reopen')
                : t('feedback.markResolved')}
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="flex flex-col gap-4">
          <DescriptionCard feedback={f} />
          <ScreenshotsCard paths={f.screenshot_paths} />
          <MetadataCard feedback={f} />
          <AdminCommentCard
            value={adminComment}
            onChange={setLocalComment}
            dirty={commentDirty}
            onSave={saveCommentOnly}
            saving={mut.isPending}
          />
        </div>
        <div>
          <DebugTimelineCard
            events={debugQ.data ?? []}
            loading={debugQ.isLoading}
            feedbackTs={f.created_at}
          />
        </div>
      </div>
    </div>
  )
}

function DescriptionCard({ feedback }: { feedback: UserFeedbackRow }) {
  const { t } = useTranslation()
  const userLine = feedback.user_full_name?.trim() || feedback.user_phone || feedback.user_id
  return (
    <Card title={t('feedback.descriptionCard')}>
      <div className="space-y-3">
        <div className="text-xs text-[color:var(--stryzh-color-text-secondary)]">
          {t('feedback.sentBy', { user: userLine ?? '—' })}
        </div>
        <div className="whitespace-pre-wrap break-words text-sm text-[color:var(--stryzh-color-text-primary)]">
          {feedback.description}
        </div>
      </div>
    </Card>
  )
}

function ScreenshotsCard({ paths }: { paths: string[] }) {
  const { t } = useTranslation()
  const [urlByPath, setUrlByPath] = useState<Map<string, string | null>>(new Map())
  const [lightbox, setLightbox] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    void (async () => {
      const entries = await Promise.all(
        paths.map(async (p) => [p, await getFeedbackScreenshotUrl(p)] as const),
      )
      if (!alive) return
      setUrlByPath(new Map(entries))
    })()
    return () => {
      alive = false
    }
  }, [paths])

  if (paths.length === 0) {
    return (
      <Card title={t('feedback.screenshot')}>
        <div className="text-sm text-[color:var(--stryzh-color-text-secondary)]">
          {t('feedback.noScreenshot')}
        </div>
      </Card>
    )
  }

  return (
    <Card title={paths.length === 1 ? t('feedback.screenshot') : t('feedback.screenshotsCount', { count: paths.length })}>
      <div className="flex flex-wrap gap-3">
        {paths.map((p) => {
          const url = urlByPath.get(p)
          if (!url) {
            return (
              <div
                key={p}
                className="h-32 w-48 rounded border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-void)]"
              />
            )
          }
          return (
            <button
              key={p}
              type="button"
              onClick={() => setLightbox(url)}
              className="overflow-hidden rounded border border-[color:var(--stryzh-color-border-default)] transition hover:border-[color:var(--stryzh-color-accent-orange)]"
            >
              <img
                src={url}
                alt={t('feedback.screenshotAlt')}
                className="block max-h-64 w-auto object-contain"
              />
            </button>
          )
        })}
      </div>
      <Lightbox open={!!lightbox} src={lightbox} onClose={() => setLightbox(null)} />
    </Card>
  )
}

function MetadataCard({ feedback }: { feedback: UserFeedbackRow }) {
  const { t } = useTranslation()
  const rows: Array<[string, string]> = [
    [t('feedback.metaVersion'), `${feedback.app_version ?? '—'}${feedback.build_number != null ? ` (${feedback.build_number})` : ''}`],
    [t('feedback.metaPlatform'), feedback.platform ?? '—'],
    ['OS', feedback.os_version ?? '—'],
    [t('feedback.metaDevice'), feedback.device_model ?? '—'],
    [t('feedback.metaLocale'), feedback.locale ?? '—'],
    [t('feedback.metaNetwork'), feedback.network_type ?? '—'],
    [t('feedback.metaRoute'), feedback.route ?? '—'],
    ['session_id', feedback.session_id],
  ]
  const ctx = feedback.context
  const ctxStr =
    ctx && Object.keys(ctx).length > 0 ? JSON.stringify(ctx, null, 2) : null

  return (
    <Card title={t('feedback.metadata')}>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-baseline justify-between gap-3">
            <dt className="shrink-0 text-xs uppercase tracking-wide text-[color:var(--stryzh-color-text-secondary)]">
              {k}
            </dt>
            <dd className="break-all text-right font-mono text-xs text-[color:var(--stryzh-color-text-primary)]">
              {v}
            </dd>
          </div>
        ))}
      </dl>
      {ctxStr ? (
        <div className="mt-3 border-t border-[color:var(--stryzh-color-border-subtle)] pt-3">
          <div className="mb-1 text-xs uppercase tracking-wide text-[color:var(--stryzh-color-text-secondary)]">
            {t('feedback.screenContext')}
          </div>
          <pre className="overflow-x-auto rounded bg-[color:var(--stryzh-color-void)] p-2 text-xs text-[color:var(--stryzh-color-text-primary)]">
            {ctxStr}
          </pre>
        </div>
      ) : null}
    </Card>
  )
}

function AdminCommentCard({
  value,
  onChange,
  dirty,
  onSave,
  saving,
}: {
  value: string
  onChange: (v: string) => void
  dirty: boolean
  onSave: () => void
  saving: boolean
}) {
  const { t } = useTranslation()
  return (
    <Card title={t('feedback.adminComment')}>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        placeholder={t('feedback.adminCommentPlaceholder')}
        className="w-full resize-y rounded border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-void)] p-2 text-sm text-[color:var(--stryzh-color-text-primary)]"
      />
      {dirty ? (
        <div className="mt-2 flex justify-end">
          <Button onClick={onSave} disabled={saving} variant="secondary">
            {saving ? t('feedback.saving') : t('feedback.saveComment')}
          </Button>
        </div>
      ) : null}
    </Card>
  )
}

function DebugTimelineCard({
  events,
  loading,
  feedbackTs,
}: {
  events: ClientDebugEvent[]
  loading: boolean
  feedbackTs: string
}) {
  const { t } = useTranslation()
  const feedbackTime = useMemo(() => new Date(feedbackTs).getTime(), [feedbackTs])

  return (
    <Card title={t('feedback.eventsBefore')} subtitle={t('feedback.eventsBeforeSubtitle')}>
      {loading ? (
        <Loader />
      ) : events.length === 0 ? (
        <div className="text-sm text-[color:var(--stryzh-color-text-secondary)]">
          {t('feedback.eventsEmpty')}
        </div>
      ) : (
        <ol className="relative space-y-1.5 text-xs">
          {events.map((e) => (
            <DebugEventRow
              key={e.id}
              event={e}
              isAfterFeedback={new Date(e.created_at).getTime() > feedbackTime}
            />
          ))}
          <li className="mt-3 border-t border-dashed border-[color:var(--stryzh-color-accent-orange)] pt-2 text-center text-[10px] uppercase tracking-wide text-[color:var(--stryzh-color-accent-orange)]">
            {t('feedback.submitMoment')}
          </li>
        </ol>
      )}
    </Card>
  )
}

function DebugEventRow({
  event,
  isAfterFeedback,
}: {
  event: ClientDebugEvent
  isAfterFeedback: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const isError = event.outcome === 'error'
  const time = format(new Date(event.created_at), 'HH:mm:ss')
  const payloadStr =
    event.payload && typeof event.payload === 'object'
      ? JSON.stringify(event.payload, null, 2)
      : null

  return (
    <li
      className={
        'rounded border px-2 py-1.5 ' +
        (isError
          ? 'border-red-500/40 bg-red-500/10'
          : isAfterFeedback
            ? 'border-[color:var(--stryzh-color-border-subtle)] bg-white/[0.02] opacity-60'
            : 'border-[color:var(--stryzh-color-border-subtle)] bg-[color:var(--stryzh-color-void)]')
      }
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-baseline gap-2 text-left"
      >
        <span className="font-mono text-[10px] text-[color:var(--stryzh-color-text-secondary)]">
          {time}
        </span>
        <span className="font-mono text-[10px] uppercase text-[color:var(--stryzh-color-text-secondary)]">
          {event.kind}
        </span>
        <span className="font-medium text-[color:var(--stryzh-color-text-primary)]">
          {event.name}
        </span>
        {event.outcome ? (
          <span
            className={
              'font-mono text-[10px] ' +
              (isError ? 'text-red-300' : 'text-[color:var(--stryzh-color-text-secondary)]')
            }
          >
            {event.outcome}
          </span>
        ) : null}
        <span className="ml-auto truncate text-[color:var(--stryzh-color-text-secondary)]">
          {event.message}
        </span>
      </button>
      {expanded && payloadStr ? (
        <pre className="mt-1 overflow-x-auto rounded bg-black/30 p-2 text-[10px] text-[color:var(--stryzh-color-text-primary)]">
          {payloadStr}
        </pre>
      ) : null}
    </li>
  )
}

function Card({
  title,
  subtitle,
  children,
}: {
  title: React.ReactNode
  subtitle?: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-lg border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)] p-4">
      <div className="mb-3">
        <div className="text-base font-semibold text-[color:var(--stryzh-color-text-primary)]">
          {title}
        </div>
        {subtitle ? (
          <div className="mt-0.5 text-xs text-[color:var(--stryzh-color-text-secondary)]">
            {subtitle}
          </div>
        ) : null}
      </div>
      {children}
    </div>
  )
}

function BackLink() {
  const { t } = useTranslation()
  return (
    <Link
      to="/user-feedback"
      className="mb-3 inline-flex items-center text-xs text-[color:var(--stryzh-color-text-secondary)] hover:text-[color:var(--stryzh-color-text-primary)]"
    >
      {t('feedback.backToAll')}
    </Link>
  )
}

function Banner({ kind, text }: { kind: 'error' | 'muted'; text: string }) {
  const cls =
    kind === 'error'
      ? 'border-red-500/40 bg-red-500/10 text-red-200'
      : 'border-white/10 bg-white/5 text-[color:var(--stryzh-color-text-secondary)]'
  return <div className={`rounded border p-4 text-sm ${cls}`}>{text}</div>
}
